import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { formatDateTime } from './tenantClock';
import { usePermissions } from './PermissionsContext';

// The Platform Update Check's status (issue #439), in three places: the
// screen itself, the Platform Dashboard's card, and the Home Dashboard's
// one-line alert. The cards show only to Roles that can open the screen.

const SCREEN = 'global-platform-update-check';
const SCREEN_PATH = `/${SCREEN}`;

export const GREEN = { color: '#166534', background: '#ecfdf3', border: '1px solid #bbf7d0' };
export const AMBER = { color: '#b45309', background: '#fdf1e0', border: '1px solid #f0c34d' };
export const RED = { color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca' };
export const GREY = { color: '#5a6b85', background: '#eef1f5', border: '1px solid #bfc8d6' };

export const NODE_STATUS = {
  current: { label: 'Up to date', style: GREEN },
  update: { label: 'Update available', style: AMBER },
  security: { label: 'Security update available', style: RED },
};
export const SEVERITIES = [
  { key: 'critical', label: 'Critical', style: RED },
  { key: 'high', label: 'High', style: RED },
  { key: 'moderate', label: 'Moderate', style: AMBER },
  { key: 'low', label: 'Low', style: GREY },
];

export function StatusPill({ style, children }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', ...style }}>
      {children}
    </span>
  );
}

// The status, or null while loading or when the Role can't see the screen.
function usePlatformUpdateStatus() {
  const { accessFor } = usePermissions();
  const canView = accessFor(SCREEN).read;
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!canView) return;
    axios.get('/api/platform-update-check')
      .then((res) => setData(res.data.data))
      .catch(() => setData(null));
  }, [canView]);
  return canView ? data : null;
}

// Something to act on: a Node.js security update, or any Critical or High
// package vulnerability.
function needsAction(data) {
  return data.node.status === 'security' || (data.packages.current && (data.packages.counts.critical + data.packages.counts.high) > 0);
}

const CARD = { background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };
const ROW = { display: 'flex', gap: 12, padding: '3px 0', fontSize: 14 };
const LABEL = { fontSize: 13, color: '#5a6b85', width: 120, flexShrink: 0 };
const MUTED = { color: '#8a94a6', fontSize: 13 };

// Platform Dashboard: the Node.js status and the package counts.
export function PlatformUpdateCard() {
  const data = usePlatformUpdateStatus();
  if (!data) return null;
  const { node, packages } = data;
  const status = node.status && NODE_STATUS[node.status];
  return (
    <div style={CARD}>
      <div style={ROW}><div style={LABEL}>Node.js</div><div>{node.running}{node.latest && node.latest !== node.running && <span style={MUTED}> · latest {node.latest}</span>}</div></div>
      <div style={ROW}>
        <div style={LABEL}>Status</div>
        <div>{status ? <StatusPill style={status.style}>{status.label}</StatusPill> : <span style={MUTED}>Not checked yet</span>}</div>
      </div>
      <div style={ROW}>
        <div style={LABEL}>API packages</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {packages.current
            ? SEVERITIES.map((s) => (
              <StatusPill key={s.key} style={packages.counts[s.key] ? s.style : GREY}>{packages.counts[s.key]} {s.label}</StatusPill>
            ))
            : <span style={MUTED}>Not checked yet</span>}
        </div>
      </div>
      {(node.error || packages.error) && (
        <div style={{ ...ROW, color: RED.color }}>The latest check failed. Open the Platform Update Check for details.</div>
      )}
      <div style={{ ...ROW, ...MUTED, justifyContent: 'space-between', marginTop: 6 }}>
        <span>Checked {formatDateTime(node.succeeded_at || packages.succeeded_at) || 'never'}</span>
        <Link to={SCREEN_PATH}>Open Platform Update Check</Link>
      </div>
    </div>
  );
}

// Home Dashboard: one line, only when there's something to act on.
export function PlatformUpdateAlert() {
  const data = usePlatformUpdateStatus();
  if (!data || !needsAction(data)) return null;
  const { node, packages } = data;
  const parts = [];
  if (node.status === 'security') parts.push(`Node.js security update available (${node.security_releases[0]})`);
  if (packages.current) {
    const { critical, high } = packages.counts;
    if (critical + high > 0) parts.push(`${critical} Critical, ${high} High package vulnerabilities`);
  }
  return (
    <Link
      to={SCREEN_PATH}
      title="Open the Platform Update Check"
      style={{ display: 'block', ...RED, borderRadius: 8, padding: '10px 14px', fontSize: 14, fontWeight: 500, textDecoration: 'none', marginBottom: 16 }}
    >
      Platform: {parts.join(' · ')}
    </Link>
  );
}
